import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import {
  REFERRER_FIGURES_EMPTY_TEXT,
  REFERRER_INACTIVE_SUFFIX,
  formatEuro,
  formatInteger,
} from '@brimo/shared';
import { Icon } from '../../../ds/components';
import { clsx } from '../../../ds/clsx';
import type { ControllingFigures } from './api';
import styles from './figures.module.css';

/**
 * „Tippgeber-Partner" (Controlling 04, D-248): Partner | Leads | Selbstauskünfte | Deals | Reserviert | Beurkundet |
 * Volumen per external referrer partner in the period and scope, sorted by leads; inactive partners are marked.
 * A partner with submitters („Einreicher", owner decision 02.11.2026) gets a disclosure button that shows one indented
 * sub-row per person who used the partner's link (and „Ohne Einreicher" for matched contacts).
 */

type PartnerRow = ControllingFigures['referrerPartners'][number];
type SubmitterRow = PartnerRow['submitters'][number];

function NumberCell({ label, value }: { label: string; value: string }) {
  return (
    <span className={`${styles.cell} ${styles.cellNumber}`} role="cell">
      <span className={styles.cellKey}>{label}</span>
      <span>{value}</span>
    </span>
  );
}

function Figures({ row }: { row: Omit<SubmitterRow, 'key' | 'name' | 'email'> }) {
  return (
    <>
      <NumberCell label="Leads" value={formatInteger(row.leads)} />
      <NumberCell label="Selbstauskünfte" value={formatInteger(row.selfDisclosures)} />
      <NumberCell label="Deals" value={formatInteger(row.deals)} />
      <NumberCell label="Reserviert" value={formatInteger(row.reserved)} />
      <NumberCell label="Beurkundet" value={formatInteger(row.notarized)} />
      <NumberCell label="Volumen" value={formatEuro(row.volumeCents)} />
    </>
  );
}

const COLUMNS = ['Leads', 'Selbstauskünfte', 'Deals', 'Reserviert', 'Beurkundet', 'Volumen'] as const;

export function PartnerRows({
  row,
  expanded,
  onToggle,
}: {
  row: PartnerRow;
  expanded: boolean;
  onToggle: () => void;
}) {
  const expandable = row.submitters.length > 0;
  const groupId = `figures-referrer-${row.id}`;
  const name = (
    <>
      {row.name}
      {row.active ? null : <span className={styles.cellInactive}>{REFERRER_INACTIVE_SUFFIX}</span>}
    </>
  );
  return (
    <>
      <div className={`${styles.tableRow} ${styles.partnerRow}`} role="row">
        <span className={`${styles.cell} ${styles.cellName}`} role="cell">
          {expandable ? (
            <button
              type="button"
              className={clsx('pressable', styles.disclosure)}
              aria-expanded={expanded}
              aria-controls={groupId}
              aria-label={`Einreicher von ${row.name} ${expanded ? 'ausblenden' : 'anzeigen'}`}
              onClick={onToggle}
            >
              <span className={clsx(styles.disclosureIcon, expanded && styles.disclosureOpen)}>
                <Icon icon={ChevronRight} size={16} />
              </span>
              <span className={styles.disclosureText}>{name}</span>
            </button>
          ) : (
            name
          )}
        </span>
        <Figures row={{ ...row, volumeCents: row.notarizedVolumeCents }} />
      </div>
      {expandable ? (
        <div id={groupId} role="rowgroup" hidden={!expanded}>
          {expanded
            ? row.submitters.map((sub) => (
                <div
                  key={sub.key ?? 'none'}
                  className={`${styles.tableRow} ${styles.partnerRow} ${styles.submitterRow}`}
                  role="row"
                >
                  <span className={`${styles.cell} ${styles.cellName} ${styles.submitterName}`} role="cell">
                    <span className={styles.submitterLabel}>{sub.name}</span>
                    {sub.email !== null ? <span className={styles.submitterEmail}>{sub.email}</span> : null}
                  </span>
                  <Figures row={sub} />
                </div>
              ))
            : null}
        </div>
      ) : null}
    </>
  );
}

export function ReferrerPartnersTable({ rows }: { rows: ControllingFigures['referrerPartners'] }) {
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const toggle = (id: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  return (
    <section className={`${styles.card} ${styles.tableCard}`} aria-labelledby="figures-referrers-title">
      <h2 id="figures-referrers-title" className={styles.cardTitle}>
        Tippgeber-Partner
      </h2>
      <div className={styles.table} role="table" aria-labelledby="figures-referrers-title">
        <div className={`${styles.tableRow} ${styles.partnerRow} ${styles.tableHead}`} role="row">
          <span className={styles.cell} role="columnheader">
            Partner
          </span>
          {COLUMNS.map((column) => (
            <span key={column} className={`${styles.cell} ${styles.cellNumber}`} role="columnheader">
              {column}
            </span>
          ))}
        </div>
        {rows.length === 0 ? (
          <div className={styles.tableEmpty} role="row">
            <span role="cell">{REFERRER_FIGURES_EMPTY_TEXT}</span>
          </div>
        ) : null}
        {rows.map((row) => (
          <PartnerRows key={row.id} row={row} expanded={open.has(row.id)} onToggle={() => toggle(row.id)} />
        ))}
      </div>
    </section>
  );
}
